import { absoluteDay, DASH, relativeDay } from './dates';
import { museumLabel, progressText, STATUS_LABEL } from './museum-list';

import type { ActivationDetail, ActivationFeature, FeatureState } from '../schemas/activation';

/**
 * Everything the detail screen says, as pure functions.
 */

export const OPENED_BY_NOTE = 'A Veevart user. In migrated orgs this is usually the org admin.';

export const COULDNT_CHECK_NOTE =
    "The page didn't answer when it tried to check this. That's a problem on Veevart's side, not the museum's.";

export function detailTitle(detail: ActivationDetail): string {
    return museumLabel(detail);
}

export function detailStatusLabel(detail: ActivationDetail): string {
    return STATUS_LABEL[detail.status];
}

export function detailDescription(detail: ActivationDetail): string {
    return detail.activation
        ? `${progressText(detail.done, detail.total)} features done, as seen on the museum's last visit to Feature activation.`
        : "This museum has never opened Feature activation, so we don't know anything about its setup yet.";
}

/** The line beside "Features": how old everything in the table is. */
export function freshnessText(detail: ActivationDetail, now: Date = new Date()): string {
    if (!detail.activation) return 'No visits yet. Nothing has been checked.';
    const last = detail.activation.lastOpenedAt;
    return `As of ${absoluteDay(last)} (${relativeDay(last, now).toLowerCase()}), the museum's last visit. Things may have changed since.`;
}

/* ------------------------------------------------------------------ facts - */

export interface DetailFact {
    readonly label: string;
    readonly value: string;
    readonly sub: string;
}

/** Under the id when identity-api did not answer: the id is real, the email is what is missing. */
export const OPENED_BY_UNRESOLVED_NOTE = "Couldn't resolve the email";

/**
 * Who opened it, as far as we can say.
 *
 * The user is a Veevart user id, not a Salesforce one. Three answers, kept
 * apart on purpose:
 * - an email — identity-api knows the user;
 * - the bare user id — identity-api was asked and does not know it;
 * - the user id too when identity-api did not answer — the id is still a fact
 *   worth keeping — but then the line under it says the email could not be
 *   resolved ({@link openedBySub}), so it does not read as the second case.
 */
export function openedByValue(detail: ActivationDetail): string {
    const opener = detail.activation?.firstOpenedBy;
    if (!opener) return DASH;
    return opener.email ?? opener.userId;
}

/** The line under *Opened by*. */
export function openedBySub(detail: ActivationDetail): string {
    const opener = detail.activation?.firstOpenedBy;
    if (!opener) return '';
    if (!opener.email && detail.peopleLookup === 'failed') return OPENED_BY_UNRESOLVED_NOTE;
    return OPENED_BY_NOTE;
}

export function detailFacts(detail: ActivationDetail, now: Date = new Date()): DetailFact[] {
    const activation = detail.activation;
    const invited = activation?.firstSource === 'invite';

    return [
        {
            label: 'First opened',
            value: relativeDay(activation?.firstOpenedAt, now),
            sub: absoluteDay(activation?.firstOpenedAt),
        },
        {
            label: 'Came from',
            value: activation ? (invited ? 'Invitation' : 'Directly') : DASH,
            sub: activation
                ? invited
                    ? 'Opened the link in the invitation email'
                    : 'Opened the page from Salesforce'
                : '',
        },
        {
            label: 'Last visit',
            value: relativeDay(activation?.lastOpenedAt, now),
            sub: absoluteDay(activation?.lastOpenedAt),
        },
        {
            label: 'Opened by',
            value: openedByValue(detail),
            sub: openedBySub(detail),
        },
    ];
}

/* --------------------------------------------------------------- features - */

export const FEATURE_STATE_LABEL: Record<FeatureState, string> = {
    done: 'Done',
    not_done: 'Not done',
    unknown: "Couldn't check",
    no_data: 'No data',
};

export interface DatedCell {
    readonly relative: string;
    readonly absolute: string;
}

export interface FeatureRowView {
    readonly id: string;
    readonly title: string;
    readonly state: FeatureState;
    readonly stateLabel: string;
    readonly checked: DatedCell;
    readonly started: DatedCell;
    readonly seenDone: DatedCell;
}

function dated(iso: string | undefined, now: Date): DatedCell {
    return { relative: relativeDay(iso, now), absolute: absoluteDay(iso) };
}

/**
 * One feature row.
 *
 * A feature the page could not check still has a `checkedAt` — the time it
 * *tried* — so it says "Tried 3 days ago" rather than passing the attempt off
 * as a check.
 */
export function toFeatureRow(feature: ActivationFeature, now: Date = new Date()): FeatureRowView {
    const checked = dated(feature.checkedAt, now);

    return {
        id: feature.id,
        title: feature.title,
        state: feature.state,
        stateLabel: FEATURE_STATE_LABEL[feature.state],
        checked:
            feature.state === 'no_data'
                ? { relative: DASH, absolute: '' }
                : feature.state === 'unknown' && checked.relative !== DASH
                  ? { ...checked, relative: `Tried ${checked.relative.toLowerCase()}` }
                  : checked,
        started: dated(feature.startedAt, now),
        seenDone: dated(feature.doneSeenAt, now),
    };
}
